import { useEffect, useState } from "react";
import { isValidEmail } from "components/core/ManageUsers/userValidation";
import useOnboardingStore from "components/core/Onboarding/useOnboardingStore";
import useVendorStore from "../HomePage/useVendorStore";
import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";
import { OTP_LENGTH, RESEND_TIMEOUT, STATUS_BEGIN } from "utils/constants/others";
import { strings } from "utils/constants/strings";

/** Step 1 of adding an email: validate it and request an OTP. `onSent(email)` runs once the OTP is on its way. */
export function useGrnEmailEntry({ onSent }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const requestSignupOtp = useOnboardingStore((state) => state.requestSignupOtp);
  const signupOtpStatus = useOnboardingStore((state) => state.signupOtpStatus);
  const loading = signupOtpStatus === STATUS_BEGIN;

  const changeEmail = (value) => {
    setEmail(value);
    setError("");
  };

  const handleVerifyClick = async () => {
    setError("");
    if (!isValidEmail(email)) {
      setError(strings.grnEmailValidationError);
      return;
    }
    const res = await requestSignupOtp("email", email.trim());
    if (!res.ok) {
      setError(res.message || strings.grnOtpSendError);
      return;
    }
    onSent(email.trim());
  };

  return { email, changeEmail, error, loading, handleVerifyClick };
}

/** Step 2: enter the 6-digit OTP, resend after the timeout, then verify + save the email. */
export function useGrnOtpVerification({ email, onVerified }) {
  const verifyAndUpdateEmailAction = useVendorStore((state) => state.verifyAndUpdateEmail);
  const verifyAndUpdateEmailErr = useVendorStore((state) => state.verifyAndUpdateEmailErr);

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [timer, setTimer] = useState(RESEND_TIMEOUT);
  const [showResend, setShowResend] = useState(false);

  useEffect(() => {
    if (verifyAndUpdateEmailErr) {
      setShowToast(true);
    }
  }, [verifyAndUpdateEmailErr]);

  useEffect(() => {
    if (timer <= 0) {
      setShowResend(true);
      return undefined;
    }
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const handleResend = async () => {
    if (!showResend) return;
    setError("");
    setOtp("");
    setShowResend(false);
    setTimer(RESEND_TIMEOUT);
    setLoading(true);
    try {
      await api.post(API_HOST_WMS, APIendpoints.getSignupOtp, {
        email: email?.trim(),
      });
    } catch (err) {
      setError(err?.response?.data?.error?.message || strings.grnOtpResendError);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndUpdate = () => {
    setError("");
    if (otp.length < OTP_LENGTH) {
      setError(strings.grnOtpCompleteError);
      return;
    }
    setLoading(true);
    verifyAndUpdateEmailAction(
      { email: email?.trim(), otp },
      () => {
        setLoading(false);
        onVerified();
      },
      () => {
        setLoading(false);
      }
    );
  };

  return {
    otp,
    setOtp,
    loading,
    error,
    timer,
    showResend,
    handleResend,
    handleVerifyAndUpdate,
    showToast,
    setShowToast,
    verifyAndUpdateEmailErr,
    isOtpComplete: otp.length >= OTP_LENGTH,
  };
}
